// kommunikationSpaltenPlan.ts — die Spaltenaufteilung des Kommunikations-Blocks
// einer PERSON (links alles Private, rechts alles Geschäftliche) und die Regel,
// wann hinter einem geschäftlichen Feld ein Firmenname stehen darf.
//
// Der Zusatz „Plan" im Dateinamen ist kein Schmuck: macOS-Dateisysteme sind
// case-insensitiv, `./kommunikationSpalten` löste sonst auf DIESE Datei auf
// statt auf die Komponente `KommunikationSpalten.tsx` — der Import war zur
// Laufzeit `undefined`.
//
// Warum das als eigenes, reines Modul steht: bis 08/2026 lagen die acht Felder
// in EINEM durchlaufenden Raster. Welche Spalte ein Feld traf, ergab sich allein
// aus seiner Position — ab der dritten Zeile stand links „Telefax" und rechts
// „E-Mail privat". Wer ein Feld verschob, verschob stillschweigend auch seine
// Bedeutung. Steht die Spalte am Feld, kann sie nicht mehr verrutschen und ist
// prüfbar.

export const KOMM_SPALTEN = ["privat", "geschaeftlich"] as const;
export type KommSpalte = (typeof KOMM_SPALTEN)[number];

/** Überschrift der Spalte — sie trägt die Unterscheidung, nicht mehr das Label. */
export const KOMM_SPALTEN_TITEL: Record<KommSpalte, string> = {
  privat: "Privat",
  geschaeftlich: "Geschäftlich",
};

/**
 * Woher der Wert eines Feldes kommt:
 *  - `geteilt`   — je Spalte ein eigener Wert (commValue/setComm mit `kind`)
 *  - `fax`       — EIN Wert für den ganzen Kontakt (faxValue/setFax)
 *  - `homepage`  — EIN Wert für den ganzen Kontakt (commValueFirma/setCommFirma)
 */
export type KommBindung = "geteilt" | "fax" | "homepage";

export interface KommFeldPlan {
  /** Stabiler React-Key. */
  key: string;
  spalte: KommSpalte;
  typ: "phone" | "mobile" | "email" | "fax" | "homepage";
  bindung: KommBindung;
  /**
   * Beschriftung OHNE „privat"/„geschäftlich": das sagt bereits die
   * Spaltenüberschrift, die auch beim Umbruch auf eine Spalte über der Gruppe
   * stehen bleibt.
   */
  label: string;
  ph: string;
  action: "phone" | "mail" | "web";
  /**
   * Feldschlüssel der Live-Präsenz. Unverändert aus dem Bestand übernommen —
   * ein neuer Schlüssel hieße, dass zwei Kollegen im selben Feld einander nicht
   * mehr sehen, solange einer eine ältere Version offen hat.
   */
  presenceKey: string;
  /** true = darf den Namen der zugehörigen Firma tragen. */
  mitFirma: boolean;
}

/**
 * Zielbild des Product Owners (18.08.2026):
 *
 *   Privat                     Geschäftlich
 *   Telefon                    Telefon      (Firma)
 *   Mobil                      Mobil        (Firma)
 *   E-Mail                     E-Mail       (Firma)
 *   Telefax                    Website
 *
 * Telefax und Website kennen keine Privat/Geschäftlich-Trennung; sie füllen die
 * letzte Zeile, damit beide Spalten gleich viele Zeilen haben und auf einer
 * Linie enden.
 */
export const KOMM_FELDER_PERSON: readonly KommFeldPlan[] = [
  { key: "phone-privat", spalte: "privat", typ: "phone", bindung: "geteilt", label: "Telefon", ph: "Telefon", action: "phone", presenceKey: "comm:phone:privat", mitFirma: false },
  { key: "phone-geschaeftlich", spalte: "geschaeftlich", typ: "phone", bindung: "geteilt", label: "Telefon", ph: "Telefon", action: "phone", presenceKey: "comm:phone:geschaeftlich", mitFirma: true },
  { key: "mobile-privat", spalte: "privat", typ: "mobile", bindung: "geteilt", label: "Mobil", ph: "Mobil", action: "phone", presenceKey: "comm:mobile:privat", mitFirma: false },
  { key: "mobile-geschaeftlich", spalte: "geschaeftlich", typ: "mobile", bindung: "geteilt", label: "Mobil", ph: "Mobil", action: "phone", presenceKey: "comm:mobile:geschaeftlich", mitFirma: true },
  { key: "email-privat", spalte: "privat", typ: "email", bindung: "geteilt", label: "E-Mail", ph: "E-Mail", action: "mail", presenceKey: "comm:email:privat", mitFirma: false },
  { key: "email-geschaeftlich", spalte: "geschaeftlich", typ: "email", bindung: "geteilt", label: "E-Mail", ph: "E-Mail", action: "mail", presenceKey: "comm:email:geschaeftlich", mitFirma: true },
  { key: "fax", spalte: "privat", typ: "fax", bindung: "fax", label: "Telefax", ph: "Telefax", action: "phone", presenceKey: "comm:fax", mitFirma: false },
  { key: "homepage", spalte: "geschaeftlich", typ: "homepage", bindung: "homepage", label: "Website", ph: "www.muster.de", action: "web", presenceKey: "comm:homepage", mitFirma: false },
];

/** Felder einer Spalte in Anzeigereihenfolge. */
export function kommFelderDerSpalte(spalte: KommSpalte): KommFeldPlan[] {
  return KOMM_FELDER_PERSON.filter((f) => f.spalte === spalte);
}

/** Art-Wert für commValue/setComm (die dortige Signatur nutzt das deutsche Wort). */
export function kommArt(spalte: KommSpalte): "privat" | "geschäftlich" {
  return spalte === "privat" ? "privat" : "geschäftlich";
}

// --- Firmenzuordnung hinter den geschäftlichen Feldern ----------------------

/** Eine mit der Person verbundene Firma (aus derivePersonEmployment). */
export interface VerbundeneFirma {
  id: number;
  name: string;
}

/** Eine Zeile aus ContactCommunication — nur die hier gelesenen Spalten. */
export interface KommZeile {
  type?: string;
  kind?: string | null;
  label?: string | null;
  companyId?: number | null;
  /** Alle Unternehmen des Wegs (API, seit 02.10.2026). */
  companies?: Array<{ id: number; name: string }> | null;
}

/**
 * Die Namen der Unternehmen, die an einer Zeile GESPEICHERT sind — in
 * Reihenfolge. Die API liefert sie mit Namen (`companies`); fehlen die, wird
 * `companyId` über die verbundenen Firmen aufgelöst (ältere Antworten).
 */
export function firmenDerZeile(
  zeile: KommZeile | null | undefined,
  verbunden: readonly VerbundeneFirma[] | null | undefined,
): string[] {
  if (!zeile) return [];
  if (Array.isArray(zeile.companies)) {
    return zeile.companies.map((f) => String(f?.name ?? "").trim()).filter(Boolean);
  }
  if (zeile.companyId == null) return [];
  const name = (verbunden ?? []).find((f) => f.id === zeile.companyId)?.name;
  return name ? [name] : [];
}

/**
 * Findet die geschäftliche Zeile eines Typs in derselben Reihenfolge wie
 * `commValue` in useContactGeneralForm: strukturiertes `kind` gewinnt,
 * Bestandszeilen ohne `kind` werden über ihr Freitext-Label gefunden.
 */
export function geschaeftlicheKommZeile(
  comms: readonly KommZeile[] | null | undefined,
  typ: string,
): KommZeile | undefined {
  const liste = comms ?? [];
  return (
    liste.find((c) => c.type === typ && c.kind === "geschaeftlich") ??
    liste.find(
      (c) => c.type === typ && c.kind == null && (c.label ?? "").toLowerCase() === "geschäftlich",
    )
  );
}

/**
 * Die Unternehmen, die hinter einem geschäftlichen Feld stehen dürfen — leer,
 * wenn die Zuordnung nicht belegt ist. Seit 02.10.2026 eine LISTE (eine Nummer
 * kann mehreren Unternehmen gehören); die Anzeige kürzt `firmenAnzeige`.
 *
 * Angezeigt wird nur, was IM DATENMODELL steht, nie etwas Geratenes:
 *  1. Die am Kommunikationsweg gespeicherten Unternehmen (Join-Tabelle,
 *     `companies` der API) — sie schlagen alles andere, auch ein inzwischen
 *     ausgeschiedenes Unternehmen bleibt sichtbar, solange es zugeordnet ist.
 *  2. Trägt die Zeile keins, aber die Person ist mit GENAU EINER Firma
 *     verbunden, kann nur diese gemeint sein.
 *  3. Sonst nichts. Mehrere Firmen ohne gespeicherte Zuordnung heißt: unbekannt.
 *     Eine falsche Firma an einer Telefonnummer ist schlimmer als gar keine.
 *
 * Ältere Antworten ohne `companies`: Zeigt `companyId` auf eine Firma, die
 * nicht (mehr) verbunden ist, bleibt es leer — der Name ist dann unbekannt.
 */
export function firmaFuerGeschaeftlichesFeld(
  comms: readonly KommZeile[] | null | undefined,
  typ: string,
  firmen: readonly VerbundeneFirma[] | null | undefined,
): string[] {
  const verbunden = firmen ?? [];
  const zeile = geschaeftlicheKommZeile(comms, typ);
  if (zeile && (Array.isArray(zeile.companies) ? zeile.companies.length > 0 : zeile.companyId != null)) {
    return firmenDerZeile(zeile, verbunden);
  }
  return verbunden.length === 1 ? [verbunden[0].name] : [];
}
